import { DateTime } from 'luxon';
export const pathKey = (...parts) => parts.filter(Boolean).join(".");

const isBlank = (v) =>
  v === undefined || v === null ||
  (typeof v === 'string' && v.trim() === '') ||
  (Array.isArray(v) && v.length === 0);

const add = (errors, key, msg) => {
  errors[key] = msg;
};

const posInt = (v) => Number.isInteger(v) && v > 0;
const posNum = (v) => typeof v === 'number' && v > 0;

function feePolicies(rules = [], modeData = {}) {
  const e = {};
  if (rules.length === 0) return e;

  let hasAllScope = false;
  
  const used = {
    personal_services: new Map(),
    table_inventory: new Map(),
    class_based: new Map(),
    accommodation: new Map(),
  };

  rules.forEach((fp, i) => {
    const p = `fee_policies${i}`;
    if (isBlank(fp.label)) add(e, `${p}.label`, 'Label required');

    const f = fp.fee ?? {};

    /* ── charge-before ── */
    const hasPrePay = posNum(f.prepayment) || f.full_prepayment_required === true;
    if (hasPrePay) {
      if (!posInt(f.charge_before_value))
        add(e, `${p}.charge_before`, 'Enter charge-before time');
      if (!['hours', 'days'].includes(f.charge_before_unit))
        add(e, `${p}.charge_before_unit`, 'Select hours or days');
    }
    if (!Number.isInteger(f.prepayment))
      add(e, `${p}.prepayment`, 'Specify pre-payment amount');

    /* ── no-show timeout ── */
    if (!posInt(f.no_show_timeout_value))
      add(e, `${p}.timeout`, 'Enter timeout > 0');
    if (!['minutes', 'hours'].includes(f.no_show_timeout_unit))
      add(e, `${p}.timeoutUnit`, 'Select minutes or hours');

    /* ── cancellation vs charge-before ── */
    const chargeHours = hasPrePay
      ? (f.charge_before_unit === 'days'
          ? f.charge_before_value * 24
          : f.charge_before_value)
      : 0;

    (f.cancellation_fee_rules || []).forEach((r, ri) => {
      const rp = `${p}.cancel${ri}`;
      if (!posInt(r.time_value)) add(e, `${rp}.time`, 'Enter time > 0');
      if (!['hours', 'days'].includes(r.time_unit)) add(e, `${rp}.unit`, 'Select hours/days');
      if (r.amount === undefined || r.amount < 0)
        add(e, `${rp}.amount`, 'Amount must be ≥ 0');

      const cancelHours = r.time_unit === 'days' ? r.time_value * 24 : r.time_value;
      if (cancelHours < chargeHours && posNum(r.amount))
        add(e, `${rp}.time`, 'Must be ≥ “Charge Before” unless the fee is 0');
    });

    /* ── scope enforcement ── */
    if (fp.scope === 'all') {
      if (hasAllScope) {
        add(e, `${p}.scope`, 'Only one fee policy can apply to all offerings');
      } else {
        hasAllScope = true;
        // If 'all' is selected, no other custom policy is allowed to have any offerings
        rules.forEach((other, j) => {
          if (i === j) return;
          if (other.scope === 'custom') {
            const used = Object.values(other.applies_to || {}).some((arr) => (arr ?? []).length > 0);
            if (used) {
              add(e, `fee_policies${j}.scope`, 'Cannot combine "All offerings" with other policies');
              add(e, `${p}.scope`, 'Cannot combine "All offerings" with other policies');
            }
          }
        });
      }
    } else if (fp.scope === 'custom') {
      let total = 0;

      Object.entries(fp.applies_to || {}).forEach(([mode, ids]) => {
        (ids ?? []).forEach((id) => {
          total++;
          const seen = used[mode];
          if (seen.has(id)) {
            const otherIdx = seen.get(id);
            add(e, `fee_policies${otherIdx}.scope`, 'Offering is used in multiple policies');
            add(e, `${p}.scope`, 'Offering is used in multiple policies');
          } else {
            seen.set(id, i);
          }
        });
      });

      if (total === 0) {
        add(e, `${p}.scope`, 'Pick at least one offering');
      }
    }
  });

  return e;
}


export function operationalHours(rules = [], modeData = {}, modes = []) {
  const e = {};


  if (rules.length === 0) {
    add(e, "operational_hours_rules", "Add at least one rule");
    return e;
  }

  // Cap: at most 10 rules total
  if (Array.isArray(rules) && rules.length > 10) {
    add(e, "operational_hours_rules", "Maximum 10 operational-hours rules");
  }

  const mins = (iso) => {
    if (!iso) return null;                     // guard against null / undefined
  
    const timeStr = iso.length > 5             // “2025-07-08T09:00:00Z” vs “09:00”
      ? iso.slice(11, 16)
      : iso;
  
    const [h, mm] = timeStr.split(":").map(Number);
    if (Number.isNaN(h) || Number.isNaN(mm)) return null;
    return h * 60 + mm;
  };

  const seatingTaken = new Map(); // seatingCfgId → ruleIdx

  rules.forEach((r, i) => {
    const p = `operational_hours_rules${i}`;

    /* 1️⃣  Basic presence */
    if (isBlank(r.label)) add(e, `${p}.label`, "Label required");

    /* 2️⃣  Hours per day  (order, overlap, ≤5) */
    Object.entries(r.hours || {})
      .filter(([day]) => day !== 'exceptions')   // ⬅️ skip exceptions
      .forEach(([day, def]) => {      if (def.closed) return; // closed day – fine

      const slots = Array.isArray(def.slots) ? def.slots : [];
      if (slots.length === 0)
        add(e, `${p}.hours`, "Define opening hours");

      if (slots.length > 5)
        add(e, `${p}.hours`, "Maximum 5 time ranges per day");

      const validSlots = slots.filter((s) => s.open && s.close);
      const sorted = [...validSlots].sort((a, b) => mins(a.open) - mins(b.open));
      sorted.forEach((s, idx) => {
        if (!s.open || !s.close || mins(s.open) >= mins(s.close))
          add(e, `${p}.hours`, "Open must be before close");

        if (idx > 0 && mins(sorted[idx - 1].close) >= mins(s.open))
          add(e, `${p}.hours`, "Time ranges overlap");
      });
    });
    const openSomewhere = Object.values(r.hours || {})
      .some(def => !def.closed && (def.slots?.length > 0));

    if (!openSomewhere) {
      add(e, `${p}.hours`, 'At least one day must be open each week');
    }

    (r.hours?.exceptions || []).forEach((ex, exIdx) => {
      const xp = `${p}.exception${exIdx}`;
  
      // A. date present & unique
      if (isBlank(ex.date))               add(e, `${xp}.date`, 'Pick a date');
      else {
        const dup = (r.hours.exceptions ?? [])
          .some((o, j) => j !== exIdx && o.date === ex.date);
        if (dup)                          add(e, `${xp}.date`, 'Duplicate date');
      }
  
      // B. if CLOSED we ignore slots (they will be stripped in UI – see below)
      if (ex.closed) return;
  
      // C. at least one slot
      const slots = Array.isArray(ex.slots) ? ex.slots : [];
      if (slots.length === 0)             add(e, `${xp}.slots`, 'Add time range(s)');
  
      // D. slot order / overlap / touching
      const byOpen = [...slots]
        .filter(s => s.open && s.close)
        .sort((a, b) => mins(a.open) - mins(b.open));
  
      byOpen.forEach((s, si) => {
        if (!s.open || !s.close || mins(s.open) >= mins(s.close))
        if (!r.label || String(r.label).trim() === '') add(e, `${p}.label`, 'Label required');
        const scope = r.scope || 'all';
        if (!['all','custom'].includes(scope)) add(e, `${p}.scope`, 'Scope required');
          add(e, `${xp}.slots`, 'Open must be before close');
  
        if (si > 0 && mins(byOpen[si - 1].close) >= mins(s.open))
          add(e, `${xp}.slots`, 'Time ranges overlap or touch');
      });
    });

    /* 3️⃣  Scope pick */
    if (r.scope === "custom") {
      const picked = Object.values(r.applies_to || {}).reduce(
        (sum, arr) => sum + (arr?.length || 0),
        0
      );
      if (picked === 0)
        add(e, `${p}.scope`, "Pick at least one offering");
        if (typeof r.allow_party_size_changes !== 'boolean') add(e, `${p}.allow_party_size_changes`, 'Required');
        if (typeof r.allow_switch_service !== 'boolean') add(e, `${p}.allow_switch_service`, 'Required');
        if (typeof r.allow_switch_class !== 'boolean') add(e, `${p}.allow_switch_class`, 'Required');
        if (typeof r.allow_change_room_type !== 'boolean') add(e, `${p}.allow_change_room_type`, 'Required');
        if (typeof r.allow_change_seating_area !== 'boolean') add(e, `${p}.allow_change_seating_area`, 'Required');
    }
    /* 4️⃣  Uniqueness constraint for seating-configs */
    (r.applies_to?.table_inventory || []).forEach((id) => {
      if (seatingTaken.has(id)) {
        const prev = seatingTaken.get(id);
        add(
          e,
          `operational_hours_rules${prev}.scope`,
          "A seating configuration can belong to ONE hours rule only"
        );
        add(
          e,
          `${p}.scope`,
          "A seating configuration can belong to ONE hours rule only"
        );
      } else {
        seatingTaken.set(id, i);
      }
    });
  });
  const needsHours =
    modes.includes("table_inventory") &&
    Array.isArray(modeData.table_inventory?.services) &&
    modeData.table_inventory.services.length > 0;

  if (needsHours) {
    // A. at least one rule
    if (rules.length === 0) {
      add(e, "operational_hours_rules", "Add at least one hours rule");
      return e;
    }

    // B. “only one ALL” guard
    let idxAll = -1;
    rules.forEach((r, i) => {
      if (r.scope === "all") {
        if (idxAll === -1) idxAll = i;
        else {
          add(e, `operational_hours_rules${r.id}.scope`, "Only one rule can target ALL offerings");
          add(e, `operational_hours_rules${rules[idxAll].id}.scope`, "Only one rule can target ALL offerings");
        }
      }
    });

    // C. each seating‐config must be covered exactly once
    const seatingIds = (modeData.table_inventory?.services || []).map((service) => service.id);
    const covered = new Set();
    rules.forEach((r) => {
      if (r.scope === "all") seatingIds.forEach((id) => covered.add(id));
      ;(r.applies_to?.table_inventory || []).forEach((id) => covered.add(id));
    });
    seatingIds.forEach((id) => {
      if (!covered.has(id))
        add(e, "operational_hours_rules", "Each seating configuration must be covered by an hours rule");
    });
  }
  /* 5️⃣  Coverage – every seating-config must belong to some rule */
  const cfgIds = (modeData.table_inventory?.services || []).map((service) => service.id);
  const covered = new Set();
  rules.forEach((r) => {
    if (r.scope === "all") cfgIds.forEach((id) => covered.add(id));
    (r.applies_to?.table_inventory || []).forEach((id) => covered.add(id));
  });
  cfgIds.forEach((id) => {
    if (!covered.has(id))
      add(
        e,
        "operational_hours_rules",
        "All seating configurations must have hours"
      );
  });

  return e;
}

function taxesAndFees(tf = {}, modeData = {}) {
  const e = {};
  const taxes = tf.taxes || [];
  if (taxes.length === 0) return e;

  const taken = new Map(); // key = `${group}:${id}` → value = taxIndex
  let idxAll = -1;
  let hasCustom = false;

  taxes.forEach((t, i) => {
    const p = `taxes${i}`;
    if (isBlank(t.label)) add(e, `${p}.label`, 'Label required');
    if (!posNum(t.rate))  add(e, `${p}.rate`,  'Enter % rate');

    const scope = t.scope ?? 'all';

    if (scope === 'all') {
      if (idxAll === -1) idxAll = i;
    }

    if (scope === 'custom') {
      const picked = Object.values(t.applies_to || {})
        .reduce((sum, arr) => sum + (arr?.length || 0), 0);

      if (picked === 0) {
        add(e, `${p}.scope`, 'Pick at least one offering');
        return;
      }

      hasCustom = true;

      // Uniqueness check
      Object.entries(t.applies_to || {}).forEach(([group, ids]) => {
        ids?.forEach((id) => {
          const sig = `${group}:${id}`;
          if (taken.has(sig)) {
            const prevIdx = taken.get(sig);
            add(e, `taxes${prevIdx}.scope`, 'An offering can belong to ONE tax only');
            add(e, `${p}.scope`, 'An offering can belong to ONE tax only');
          } else {
            taken.set(sig, i);
          }
        });
      });
    }
  });

  // Conflict between "all" and custom
  if (idxAll !== -1 && hasCustom) {
    add(e, `taxes${idxAll}.scope`, 'Cannot combine “All offerings” with specific selections');
    taxes.forEach((t, i) => {
      if (i !== idxAll && t.scope === 'custom') {
        const picked = Object.values(t.applies_to || {})
          .some(arr => arr?.length);
        if (picked) {
          add(e, `taxes${i}.scope`, 'Cannot combine “All offerings” with specific selections');
        }
      }
    });
  }

  return e;
}


function ageRestrictions(rules = [], modeData = {}) {
  const e = {};
  if (rules.length === 0) return e;

  const taken = new Map(); // key = `${group}:${id}`

  let hasAllScope = false;
  let idxAll = -1;

  // Track which rule uses "all offerings" scope
  rules.forEach((r, i) => {
    const p = `age_restrictions${i}`;

    if (r.scope === "all") {
      if (hasAllScope) {
        add(e, `${p}.scope`, "Only one rule can apply to all offerings");
      } else {
        hasAllScope = true;
        idxAll = i;
      }
    }
  });

  const hasOfferings = (r) =>
    Object.values(r.applies_to || {}).some((arr) => arr?.length);

  const hasCustomPicks = rules.some((r) => r.scope === "custom" && hasOfferings(r));

  rules.forEach((r, i) => {
    const p = `age_restrictions${i}`;

    // ── Label check ─────────────────────────────
    if (isBlank(r.label)) {
      add(e, `${p}.label`, "Label required");
    }

    // ── Age range check ─────────────────────────
    const min = r.min_age;
    const max = r.max_age;
    if (min === undefined || max === undefined) {
      add(e, `${p}.age`, "Specify both min and max age");
    } else if (min >= max) {
      add(e, `${p}.age`, "Min age must be less than max age");
    }

    // ── Scope: custom must select something ─────
    if (r.scope === "custom") {
      const picked = Object.values(r.applies_to || {}).reduce(
        (sum, arr) => sum + (arr?.length || 0),
        0
      );
      if (picked === 0) {
        add(e, `${p}.scope`, "Pick at least one offering");
      }
    }

    // ── Uniqueness: no offering in more than one rule ─
    Object.entries(r.applies_to || {}).forEach(([group, ids]) => {
      ids?.forEach((id) => {
        const sig = `${group}:${id}`;
        if (taken.has(sig)) {
          const prevIdx = taken.get(sig);
          add(
            e,
            `age_restrictions${prevIdx}.scope`,
            "An offering can belong to ONE age rule only"
          );
          add(
            e,
            `${p}.scope`,
            "An offering can belong to ONE age rule only"
          );
        } else {
          taken.set(sig, i);
        }
      });
    });
  });

  // ── Conflict: cannot combine "all" with custom picks ─
  if (hasAllScope && hasCustomPicks) {
    add(
      e,
      `age_restrictions${idxAll}.scope`,
      "Cannot combine “All Offerings” with specific selections"
    );

    rules.forEach((r, i) => {
      if (i !== idxAll && r.scope === "custom" && hasOfferings(r)) {
        add(
          e,
          `age_restrictions${i}.scope`,
          "Cannot combine “All Offerings” with specific selections"
        );
      }
    });
  }

  return e;
}


function notificationsConfig(ntf = [], modeData = {}, bundles = []) {
  const e = {};
  if (ntf.length === 0) return e;

  /*  keep track of every offering → which notification it belongs to  */
  const taken = new Map();          // key = `${group}:${id}`  value = idx

  ntf.forEach((n, i) => {
    const p = `notifications${i}`;

    /* ── basic presence ──────────────────────────────────────────── */
    if (isBlank(n.label))   add(e, `${p}.label`, 'Label required');
    if (isBlank(n.message)) add(e, `${p}.msg`,   'Confirmation message');

    /* ── reminder-specific fields ───────────────────────────────── */
    if (n.reminder_enabled) {
      if (!posInt(n.hours_before))
        add(e, `${p}.hours_before`, 'Enter hours > 0');
      if (isBlank(n.reminder_message))
        add(e, `${p}.reminder_msg`, 'Reminder message required');
    }

    /* ── scope checks ───────────────────────────────────────────── */
    if (!n.apply_to_all) {
      const picked = Object.values(n.applies_to || {})
        .reduce((sum, arr) => sum + (arr?.length || 0), 0);
      if (picked === 0)
        add(e, `${p}.scope`, 'Pick at least one offering / bundle');
    }

    /* ── uniqueness: an offering may belong to ONE notification only */
    Object.entries(n.applies_to || {}).forEach(([group, ids]) => {
      ids?.forEach((id) => {
        const sig = `${group}:${id}`;
        if (taken.has(sig)) {
          const prevIdx = taken.get(sig);
          add(e, `notifications${prevIdx}.scope`,
              'An offering can belong to ONE notification only');
          add(e, `${p}.scope`,
              'An offering can belong to ONE notification only');
        } else {
          taken.set(sig, i);
        }
      });
    });
  });
  const idxAll = ntf.findIndex(n => n.apply_to_all === true);
  const hasCustomPicks = ntf.some(
    n =>
      !n.apply_to_all &&
      Object.values(n.applies_to || {}).some(arr => arr?.length)
  );

  if (idxAll !== -1 && hasCustomPicks) {
    // flag the “all-offerings” row
    add(
      e,
      `notifications${idxAll}.scope`,
      'Cannot combine “Apply to all” with specific selections'
    );
    // flag every row that picked something
    ntf.forEach((n, i) => {
      if (
        i !== idxAll &&
        Object.values(n.applies_to || {}).some(arr => arr?.length)
      ) {
        add(
          e,
          `notifications${i}.scope`,
          'Cannot combine “Apply to all” with specific selections'
        );
      }
    });
  }
  return e;
}

function personalServices(ps = {}, employees = [], equipment = []) {
  const e = {};
  const list = ps.services ?? [];
  if (list.length === 0) {
    add(e, 'personal_services.services', 'Add at least one service');
    // ❗ do *not* return – we still want equipment validation to run
  }

  const equipmentUsageCount = Array(equipment.length).fill(0);

  list.forEach((s, i) => {
    const prefix = `svc${i}`;
    const hasPhoto =
      !isBlank(s.photo_id) ||
      !isBlank(s.photo_service_id) ||
      !isBlank(s.photo_url) ||
      !isBlank(s.uploadId);

    if (isBlank(s.name)) add(e, `${prefix}.name`, 'Name required');
    if (!posInt(s.duration)) add(e, `${prefix}.duration`, 'Enter duration');
    if (!posInt(s.price?.amount)) add(e, `${prefix}.price`, 'Enter price');
    if (!hasPhoto)
      add(e, `${prefix}.photo`, 'Add a photo or pick one from another service');
    if (!Array.isArray(s.allowed_employees) || s.allowed_employees.length === 0) {
      add(e, `${prefix}.employees`, 'Select at least one employee');
    }
    /* ── Active periods – only when NOT full‑time ───────────────── */
    if (!s.full_time) {
      const periods = s.active_periods ?? [];

      if (periods.length === 0) {
        add(e, `${prefix}.periods`, 'Define at least one active period');
      } else {
        // 1️⃣ basic per‑slot sanity & bounds -----------------------
        periods.forEach((p) => {
          if (!Number.isInteger(p.from) || !posInt(p.to) || p.to <= p.from) {
            add(e, `${prefix}.periods`, 'Invalid time range');
          }
          if (p.to > s.duration) {
            add(e, `${prefix}.periods`, 'Exceeds service duration');
          }
        });

        // 2️⃣ overlap / touching check -----------------------------
        const ordered = [...periods].sort((a, b) => a.from - b.from);
        for (let j = 1; j < ordered.length; j++) {
          // "touching" means next.start ≤ prev.end
          if (ordered[j].from <= ordered[j - 1].to) {
            add(e, `${prefix}.periods`, 'Time ranges overlap or touch');
            break;
          }
        }

        // 3️⃣ lone slot that covers full duration -----------------
        if (
          ordered.length === 1 &&
          ordered[0].from === 0 &&
          ordered[0].to === s.duration
        ) {
          add(
            e,
            `${prefix}.periods`,
            'Period covers full duration – enable “Full‑time” instead'
          );
        }
      }
    }
    const seen = new Set();
    /* ── Equipment usage indices ---------------------------------- */
    (s.equipment_usage ?? []).forEach((u, j) => {
      const key = `${prefix}.equip${j}`;
      if (
        !Number.isInteger(u.equipment_index) ||
        u.equipment_index < 0 ||
        u.equipment_index >= equipment.length
      ) {
        add(e, key, 'Select valid equipment');
      } else {
        if (seen.has(u.equipment_index)) {
          add(e, key, 'This resource is already used in this service');
        } else {
          seen.add(u.equipment_index);
        }
        equipmentUsageCount[u.equipment_index] += 1;
      }
      if (!u.full_time) {
        if (!Number.isInteger(u.from_minute) || !posInt(u.to_minute))
          add(e, key, 'Specify from / to');
        else if (u.to_minute <= u.from_minute)
          add(e, key, 'Invalid usage window');
        else if (u.to_minute > s.duration)
          add(e, key, 'Exceeds service duration');
      }
    });

    /* ── Recurring booking sanity --------------------------------- */
    if (
      s.client_booking_options?.allow_recurring_booking &&
      !posInt(s.client_booking_options?.max_sessions)
    ) {
      add(e, `${prefix}.recurring`, 'Specify max sessions');
    }
  });

  /* ── RESOURCE‑POOL VALIDATION ----------------------------------- */
  equipment.forEach((eq, idx) => {
    const k = `equip${idx}`;
    if (isBlank(eq.name)) add(e, `${k}.name`, 'Name required');
    if (isBlank(eq.type)) add(e, `${k}.type`, 'Pick a type');
    if (!posInt(eq.quantity)) add(e, `${k}.quantity`, 'Must be > 0');
    if (equipmentUsageCount[idx] === 0)
      add(e, `${k}.unused`, 'This resource is never used');
  });

  return e;
}


function modularSeating(ms = [], requiresManualApproval = false) {
  const e = {};

  // helper – how many physical tables does a config have?
  const totalPhysicalTables = (list) =>
    list.reduce((sum, t) => sum + (t.quantity || 0), 0);
const byArea = {};
  ms.forEach((cfg, cIdx) => {
    const tts = cfg.tableTypes ?? [];

    /* ---------- basic presence ------------------------------- */
    if (tts.length === 0)
      add(e, `seating${cIdx}.tableTypes`, 'Add at least one table type');

    if (isBlank(cfg.name))
      add(e, `seating${cIdx}.name`, 'Configuration name required');

    /* ---------- each table type ------------------------------ */
    tts.forEach((t, tIdx) => {
      const p = `table${cIdx}-${tIdx}`;

      if (isBlank(t.name))          add(e, `${p}.name`, 'Required');
      if (!posInt(t.seatsPerTable)) add(e, `${p}.seats`, 'Enter seats');
      if (!posInt(t.quantity))      add(e, `${p}.qty`,   'Enter quantity');

      /* AREA TYPE --------------------------------------------- */
      if (t.areaType === 'Other' && isBlank(t.areaTypeOther))
        add(e, `${p}.areaTypeOther`, 'Describe area');
    /* ── new: ensure each logical “area” has ≥1 photo ── */
    // 1) bucket tables by the same key
    const areaGroups = tts.reduce((acc, tbl) => {
      const key =
        tbl.areaType === "Other"
          ? (tbl.areaTypeOther || "").trim().toLowerCase()
          : tbl.areaType;
      (acc[key] = acc[key] || []).push(tbl);
      return acc;
    }, {});

    // 2) for each bucket, if nobody has a photo, flag the FIRST one
    Object.values(areaGroups).forEach((group) => {
      const found = group.some(

        (tbl) => tbl.photo_id || tbl.photo_service_id || tbl.photo_url || tbl.uploadId
      );
      if (!found) {
        const firstIdx = tts.findIndex((t) => t.id === group[0].id);
        add(
          e,
          `table${cIdx}-${firstIdx}.photo`,
          "Upload a photo for this area"
        );
      }
    });


      const manyTables = totalPhysicalTables(tts) > 1;

      if (t.combinable && manyTables) {
        const combineWith = t.combineWith || [];

        // ✅ Must include *at least one* (self or other)
        if (combineWith.length === 0) {
          add(e, `${p}.comb`, 'Tick at least one table (including this one)');
        }

        // ✅ Symmetry check for others (not self)
        combineWith
          .filter((id) => id !== t.id)
          .forEach((id) => {
            const peer = tts.find((x) => x.id === id);
            if (
              peer &&
              peer.combinable &&
              !(peer.combineWith || []).includes(t.id)
            ) {
              add(
                e,
                `${p}.comb`,
                `Also tick “${t.name}” inside ${peer.name}`
              );
            }
          });
      }
    });

    /* ---------- wait-time & group-rules ---------------------- */
    const hasCombinables = tts.some((t) => t.combinable);

    if (
      !Number.isInteger(cfg.max_wait_time_minutes) ||
      cfg.max_wait_time_minutes < 0
    )
      add(e, `seating${cIdx}.wait`, 'Enter max wait-time (≥ 0)');

    if (Array.isArray(cfg.capacityRules) && cfg.capacityRules.length) {
      const seen = new Set();
      cfg.capacityRules.forEach((r) => {
        if (!posInt(r.size) || !posInt(r.maxGroups))
          add(
            e,
            `seating${cIdx}.capacityRules`,
            'Each rule needs a positive size and a positive “max groups”'
          );
        if (seen.has(r.size))
          add(
            e,
            `seating${cIdx}.capacityRules`,
            `Duplicate rule for size ${r.size}`
          );
        seen.add(r.size);
      });
    }

    /* GROUP-SIZE duration rules – skip if manual approval ------ */
    if (!requiresManualApproval) {
      const REQUIRED_SIZES = ['1', '2', '3', '4', '5+'];
      if (!Array.isArray(cfg.groupRules) || cfg.groupRules.length === 0) {
        add(e, `seating${cIdx}.groupRules`, 'Add duration rules');
      } else {
        const seen = new Set(cfg.groupRules.map((g) => g.size));
        const missing = REQUIRED_SIZES.filter((s) => !seen.has(s));
        if (missing.length)
          add(
            e,
            `seating${cIdx}.groupRules`,
            `Missing rule for size(s): ${missing.join(', ')}`
          );
      }
    }
  });

  return e;
}

function tableInventory(inv = {}) {
  const e = {};
  const tableTypes = inv.tableTypes || [];
  const areas = inv.areas || [];
  const zones = inv.joinZones || [];
  const services = inv.services || [];

  if (tableTypes.length === 0) add(e, 'table_inventory.tableTypes', 'Add at least one table type');
  if (areas.length === 0) add(e, 'table_inventory.areas', 'Add at least one area');
  if (areas.length > 0 && zones.length === 0) add(e, 'table_inventory.joinZones', 'Draw at least one seating zone');

  const typeIds = new Set(tableTypes.map((tt) => tt.id).filter(Boolean));
  const areaIds = new Set(areas.map((area) => area.id).filter(Boolean));
  const tableTypeById = new Map(tableTypes.map((tt) => [tt.id, tt]));

  tableTypes.forEach((tableType, index) => {
    if (isBlank(tableType.name)) add(e, `table_inventory.tableType${index}.name`, 'Table type name required');
    if (!Number.isInteger(tableType.minSeats) || tableType.minSeats < 0) {
      add(e, `table_inventory.tableType${index}.minSeats`, 'Minimum seats must be 0 or more');
    }
    if (!Number.isInteger(tableType.maxSeats) || tableType.maxSeats < (tableType.minSeats || 0)) {
      add(e, `table_inventory.tableType${index}.maxSeats`, 'Maximum seats must be at least the minimum');
    }
    if (!Number.isInteger(tableType.quantity) || tableType.quantity < 0) {
      add(e, `table_inventory.tableType${index}.quantity`, 'Quantity must be 0 or more');
    }
    if (tableType.joinable && (!Array.isArray(tableType.joinWithTypeIds) || tableType.joinWithTypeIds.length === 0)) {
      add(e, `table_inventory.tableType${index}.joinWithTypeIds`, 'Select at least one join target');
    }
    if (tableType.seatLossPerJoin != null && (!Number.isInteger(tableType.seatLossPerJoin) || tableType.seatLossPerJoin < 0)) {
      add(e, `table_inventory.tableType${index}.seatLossPerJoin`, 'Seat loss must be 0 or more');
    }
    Object.entries(tableType.seatLossPairOverride || {}).forEach(([key, value]) => {
      const [leftId, rightId] = key.split('|');
      if (!typeIds.has(leftId) || !typeIds.has(rightId)) {
        add(e, `table_inventory.tableType${index}.seatLossPairOverride`, 'Seat-loss override uses an invalid table type');
      }
      if (!Number.isInteger(value) || value < 0) {
        add(e, `table_inventory.tableType${index}.seatLossPairOverride`, 'Seat-loss overrides must be 0 or more');
      }
    });
  });

  areas.forEach((area, index) => {
    if (isBlank(area.name)) add(e, `table_inventory.area${index}.name`, 'Area name required');

    if (area.meta?.canvasWidth != null && (!Number.isFinite(Number(area.meta.canvasWidth)) || Number(area.meta.canvasWidth) < 1)) {
      add(e, `table_inventory.area${index}.canvasWidth`, 'Canvas width must be greater than 0');
    }
    if (area.meta?.canvasHeight != null && (!Number.isFinite(Number(area.meta.canvasHeight)) || Number(area.meta.canvasHeight) < 1)) {
      add(e, `table_inventory.area${index}.canvasHeight`, 'Canvas height must be greater than 0');
    }

    (area.meta?.infoItems || []).forEach((detail, detailIndex) => {
      if (isBlank(detail.text)) {
        add(e, `table_inventory.area${index}.info${detailIndex}`, 'Area information lines cannot be empty');
      }
    });
  });

  zones.forEach((zone, index) => {
    if (!areaIds.has(zone.areaId)) add(e, `table_inventory.zone${index}.areaId`, 'Zone must belong to an existing area');
    if (isBlank(zone.name)) add(e, `table_inventory.zone${index}.name`, 'Zone name required');
    if (!Array.isArray(zone.allowedTableTypeIds) || zone.allowedTableTypeIds.length === 0) {
      add(e, `table_inventory.zone${index}.allowedTableTypeIds`, 'Select at least one table type for this zone');
    }

    (zone.allowedTableTypeIds || []).forEach((typeId) => {
      if (!typeIds.has(typeId)) add(e, `table_inventory.zone${index}.allowedTableTypeIds`, 'Zone uses an invalid table type');
    });

    Object.entries(zone.capacityProfile?.tableTypeCapacities || {}).forEach(([typeId, cap]) => {
      if (!typeIds.has(typeId)) add(e, `table_inventory.zone${index}.capacity`, 'Capacity references an invalid table type');
      if (cap?.unjoinedFits != null && (!Number.isInteger(cap.unjoinedFits) || cap.unjoinedFits < 0)) {
        add(e, `table_inventory.zone${index}.capacity`, 'Unjoined fit count must be 0 or more');
      }
      if (cap?.joinedFits != null && (!Number.isInteger(cap.joinedFits) || cap.joinedFits < 0)) {
        add(e, `table_inventory.zone${index}.capacity`, 'Joined fit count must be 0 or more');
      }
    });

    const fixedTotalsByType = {};
    (zone.capacityProfile?.fixedTables || []).forEach((rule, ruleIndex) => {
      const tableType = tableTypeById.get(rule.tableTypeId);
      if (!zone.allowedTableTypeIds?.includes(rule.tableTypeId)) {
        add(e, `table_inventory.zone${index}.fixed${ruleIndex}`, 'Fixed table rules can only use table types already allowed in the zone');
      }
      if (!tableType) {
        add(e, `table_inventory.zone${index}.fixed${ruleIndex}`, 'Fixed table rule references an invalid table type');
      }
      if (!Number.isInteger(rule.quantity) || rule.quantity <= 0) {
        add(e, `table_inventory.zone${index}.fixed${ruleIndex}`, 'Fixed table quantity must be greater than 0');
      }
      if (rule.allowJoin && !tableType?.joinable) {
        add(e, `table_inventory.zone${index}.fixed${ruleIndex}`, 'Only joinable table types can be marked as joinable fixed tables');
      }

      fixedTotalsByType[rule.tableTypeId] = (fixedTotalsByType[rule.tableTypeId] || 0) + (Number(rule.quantity) || 0);
    });

    Object.entries(fixedTotalsByType).forEach(([typeId, quantity]) => {
      const tableType = tableTypeById.get(typeId);
      if (tableType && quantity > Number(tableType.quantity || 0)) {
        add(e, `table_inventory.zone${index}.fixed`, 'Fixed table rules cannot reserve more tables than exist in inventory');
      }
    });

    (zone.capacityProfile?.combinations || []).forEach((combo, comboIndex) => {
      if (!Array.isArray(combo.entries) || combo.entries.length === 0) {
        add(e, `table_inventory.zone${index}.combo${comboIndex}`, 'Add at least one entry to each combination row');
      }
      (combo.entries || []).forEach((entry) => {
        if (!zone.allowedTableTypeIds?.includes(entry.tableTypeId)) {
          add(e, `table_inventory.zone${index}.combo${comboIndex}`, 'Combination rows can only use table types already allowed in the zone');
        }
        if (!Number.isInteger(entry.quantity) || entry.quantity <= 0) {
          add(e, `table_inventory.zone${index}.combo${comboIndex}`, 'Each combination entry quantity must be greater than 0');
        }
        if (entry.mode === 'joined') {
          if (!Number.isInteger(entry.clusterSize) || entry.clusterSize < 2) {
            add(e, `table_inventory.zone${index}.combo${comboIndex}`, 'Joined entries need a set size of at least 2');
          }
          if (!Number.isInteger(entry.tablesPerSet) || entry.tablesPerSet <= 0) {
            add(e, `table_inventory.zone${index}.combo${comboIndex}`, 'Joined entries need at least one table of that type per set');
          }
          if (Number.isInteger(entry.clusterSize) && Number.isInteger(entry.tablesPerSet) && entry.tablesPerSet > entry.clusterSize) {
            add(e, `table_inventory.zone${index}.combo${comboIndex}`, 'Tables per set cannot exceed the joined set size');
          }
        }
      });
    });
  });

  services.forEach((service, index) => {
    if (isBlank(service.name)) add(e, `table_inventory.service${index}.name`, 'Service name required');
    (service.areaIds || []).forEach((areaId) => {
      if (!areaIds.has(areaId)) {
        add(e, `table_inventory.service${index}.areaIds`, 'Service uses an invalid area');
      }
    });
  });

  return e;
}

function classBased(cb = {}, employees = []) {
  const e     = {};
  const list  = cb.classes ?? [];

  if (list.length === 0) {
    add(e, 'class_based.classes', 'Add at least one class');
    return e;
  }

  // Track instructor schedules across all classes: map of "instructor|day" -> array of {s,e,clsIdx,sessionIdx}
  const instructorSchedules = new Map();

  list.forEach((c, i) => {
    const p       = `class${i}`;
    const sessions = c.sessions ?? [];

    /* ── core fields ───────────────────────────────────────────── */
    if (isBlank(c.title))        add(e, `${p}.name`,        'Title required');
    if (isBlank(c.description))  add(e, `${p}.description`, 'Description required');
    if (!posInt(c.duration))     add(e, `${p}.duration`,    'Enter duration');
    if (!posInt(c.capacity))     add(e, `${p}.capacity`,    'Enter capacity');
    if (!posInt(c.price?.amount))add(e, `${p}.price`,       'Enter price');

    /* dates ------------------------------------------------------ */
    if (!c.start_date)           add(e, `${p}.start`, 'Start date required');
    if (!c.end_date)             add(e, `${p}.end`,   'End date required');
    if (c.start_date && c.end_date &&
        DateTime.fromISO(c.end_date) < DateTime.fromISO(c.start_date)) {
      add(e, `${p}.dates`, 'End date is before start date');
    }

    /* recurring booking ----------------------------------------- */
    if (c.allow_recurring_booking && !posInt(c.max_recurring_sessions)) {
      add(e, `${p}.maxRecurring`, 'Enter max sessions');
     }
    if (
    isBlank(c.photo_id) &&
    isBlank(c.photo_service_id) &&
    isBlank(c.uploadId) &&          // <-- new
    isBlank(c.photo_url))
      add(e, `${p}.photo`, 'Add a photo or pick one from another class');

    /* ── sessions ------------------------------------------------ */
    if (sessions.length === 0) {
      add(e, `${p}.sessions`, 'Add at least one session');
    } else {
      sessions.forEach((s, si) => {
        const sp = `${p}.session${si}`;
        if (isBlank(s.day_of_week))  add(e, `${sp}.day`,  'Choose day');
        if (isBlank(s.start_time))   add(e, `${sp}.time`, 'Start time required');

        /* instructor – only when the toggle is on */
        if (c.show_instructor_to_clients) {
          if (isBlank(s.instructor_id)) {
            add(e, `${sp}.instructor`, 'Select instructor');
          } else if (!employees?.some(emp => emp.id === s.instructor_id)) {
            add(e, `${sp}.instructor`, 'Invalid instructor');
          } else if (!isBlank(s.day_of_week) && !isBlank(s.start_time) && posInt(c.duration)) {
            // compute minute ranges
            const toMin = (t) => {
              if (!t) return null;
              const [hh, mm] = String(t).split(':').map(Number);
              if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
              return hh * 60 + mm;
            };
            const startMin = toMin(s.start_time);
            const endMin = startMin === null ? null : startMin + Number(c.duration || 0);
            if (startMin === null || endMin === null) {
              add(e, `${sp}.time`, 'Invalid start time');
            } else {
              const key = `${s.instructor_id}|${s.day_of_week}`;
              const arr = instructorSchedules.get(key) || [];
              const overlap = arr.some(iv => iv.s < endMin && iv.e > startMin);
              if (overlap) {
                add(e, `${sp}.instructor`, 'Instructor has another session overlapping this time');
              }
              arr.push({ s: startMin, e: endMin, classIdx: i, sessionIdx: si });
              instructorSchedules.set(key, arr);
            }
          }
        }
      });
    }
  });

  return e;
}

export function accommodation(ac = {}) {
  const e = {};
  const rooms = ac.room_types ?? [];
  const ORD = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  const toOrdinal = (iso) => {
    // iso like "2025-08-20"
    if (!iso || iso.length < 10) return null;
    const mm = parseInt(iso.slice(5, 7), 10);
    const dd = parseInt(iso.slice(8, 10), 10);
    if (!mm || !dd) return null;
    const before = ORD.slice(0, mm - 1).reduce((a, b) => a + b, 0);
    return before + Math.min(dd, ORD[mm - 1]); // clamp (guards weird inputs)
  };

  // ---- Check‑in / Check‑out presence & order ------------------
  const ci = ac.check_in_after;
  const co = ac.check_out_before;
  const mins = (iso) => {
    if (!iso) return null;
    const timeStr = iso.length > 5 ? iso.slice(11, 16) : iso; // “HH:MM”
    const [h, m] = timeStr.split(":" ).map(Number);
    return Number.isNaN(h) || Number.isNaN(m) ? null : h * 60 + m;
  };

  if (isBlank(ci)) add(e, "accommodation.check_in_after", "Select check‑in time");
  if (isBlank(co)) add(e, "accommodation.check_out_before", "Select check‑out time");


  if (rooms.length === 0) {
    add(e, "accommodation.room_types", "Add at least one room type");
    return e;
  }

  rooms.forEach((r, i) => {
    const p = `room${i}`;
    const seasons = r.seasonal_pricing || [];
    /* ── basics ───────────────────────────────────────────── */
    if (isBlank(r.name)) add(e, `${p}.name`, "Name required");
    if (!posInt(r.quantity)) add(e, `${p}.quantity`, "Enter quantity");
    if (!posInt(r.max_guests)) add(e, `${p}.maxGuests`, "Enter max guests");
    if (isBlank(r.amenities)) add(e, `${p}.amenities`, "Add amenities");
    if (isBlank(r.description)) add(e, `${p}.description`, "Describe room");
    const segs = [];
    seasons.forEach((season, j) => {
      const a = toOrdinal(season.start);
      const b = toOrdinal(season.end);
      if (!a || !b) return;            // skip incomplete rows
      if (a <= b) {
        segs.push({ start: a, end: b, owner: j });
      } else {
        // wraps past Dec→Jan: split in two
        segs.push({ start: a, end: 365, owner: j });
        segs.push({ start: 1, end: b, owner: j });
      }
    });

    // check linear overlaps/touching
    segs.sort((x, y) => x.start - y.start);
    let bad = false;
    for (let k = 1; k < segs.length; k++) {
      // "touching" counts as invalid: prev.end >= curr.start
      if (segs[k].start <= segs[k - 1].end) { bad = true; break; }
    }
    // check across the year boundary (Dec 31 ↔ Jan 1)
    if (!bad && segs.length > 1) {
      const first = segs[0];
      const last = segs[segs.length - 1];
      // touching at boundary only matters if they come from different seasons
      if (first.start === 1 && last.end === 365 && first.owner !== last.owner) bad = true;
    }

    if (bad) {
      add(e, `${p}.seasons_overlap`, 'Seasonal price periods overlap or touch');
    }

    if (
      r.turnover_minutes !== undefined &&
      (!Number.isInteger(r.turnover_minutes) || r.turnover_minutes < 0)
    ) {
      add(e, `${p}.turnover`, "Turn-over must be 0 or more");
    }

    /* ── photos ──────────────────────────────────────────── */
    if (!Array.isArray(r.images) || r.images.length === 0) {
      add(e, `${p}.images`, "Upload at least one image");
    } else if (r.images.length > 10) {
      add(e, `${p}.images`, "Maximum 10 images per room");
    }

    /* ── hostel-specific ─────────────────────────────────── */
    if (r.is_hostel) {
      if (!["mixed", "female", "male"].includes(r.dorm_type)) add(e, `${p}.dormType`, "Select dorm type");
    }

    /* ── seasonal pricing validation ----------------------- */
    (r.seasonal_pricing || []).forEach((season, j) => {
      const sp = `${p}.season${j}`;
      if (isBlank(season.start)) add(e, `${sp}.start`, "Start date required");
      if (isBlank(season.end)) add(e, `${sp}.end`, "End date required");
      if (!posNum(season.price?.amount)) add(e, `${sp}.price`, "Enter price");
      if (season.start && season.end && season.start > season.end) {
        add(e, `${sp}.end`, "End date before start date");
      }
    });

    /* ── modifier validation (value & param >0) ------------- */
    (r.modifiers || []).forEach((m) => {
      if (m.type === "weekday" && (!m.param || m.param.length === 0)) {
        add(e, m.id, "Pick at least one weekday");
      }
      if (["low_inventory", "length_of_stay"].includes(m.type)) {
        if (!posInt(m.param)) add(e, m.id, "Enter number > 0");
      }
      if (typeof m.value !== "number" || m.value === 0 || !Number.isFinite(m.value))
        add(e, m.id, "Enter an amount");
    });

    /* ── duplicate-rule checks (existing helper) ------------ */
    const dupErr = checkPricingRuleDuplicates(r);
    if (dupErr) add(e, dupErr.key, dupErr.msg);
  });

  return e;
}


function bundles(bundles = []) {
  const e = {};
  if (bundles.length === 0) return e;

  const seen = new Map();

  bundles.forEach((b, i) => {
    const p = `bundles${i}`;
    if (isBlank(b.name)) add(e, `${p}.name`, "Insert bundle name");
    if (isBlank(b.description)) add(e, `${p}.description`, "Insert description");
    if (!posInt(b.price?.amount)) add(e, `${p}.price`, "Enter price");
    if (!posInt(b.consumption_window_days)) add(e, `${p}.consumption_window_days`, "Enter days ≥ 1");

    // Aggregate selected items across all modes
    const pickedItems = Object.values(b.items || {}).flat().sort();
    const pickedKey = pickedItems.join(",");

    if (pickedItems.length < 2) {
      add(e, `${p}.items`, "Select at least two offerings");
    }

    // Check for duplicate bundles
    if (pickedItems.length > 0) {
      if (seen.has(pickedKey)) {
        const dupIdx = seen.get(pickedKey);
        add(e, `${p}.items`, "This bundle duplicates another bundle's offerings");
        add(e, `bundles${dupIdx}.items`, "This bundle duplicates another bundle's offerings");
      } else {
        seen.set(pickedKey, i);
      }
    }
  });

  return e;
}

/* Hard limits helper for array counts */
function _maxCount(arr, max, key, label, e) {
  if (Array.isArray(arr) && arr.length > max) {
    add(e, key, `${label} — maximum ${max}`);
  }
}


function checkPricingRuleDuplicates(room) {
   const mods = room.modifiers ?? [];
   const seen = {
     weekdaySets : new Set(),        // for exact-dupe check (kept)
     weekdayDays : new Set(),
     low_inventory: new Set(),       // number
     length_of_stay: new Set(),      // number
   };
 
   for (const m of mods) {
     if (!['weekday', 'low_inventory', 'length_of_stay'].includes(m.type))
       continue;
 
     if (m.type === 'weekday') {
       // ── 1️⃣ prevent overlap with any *other* rule
       for (const d of m.param ?? []) {
         if (seen.weekdayDays.has(d)) {
           return {
             key: m.id,
             msg: `Weekday “${d}” is already covered by another rule`
           };
         }
       }
       m.param?.forEach(d => seen.weekdayDays.add(d));

       // ── 2️⃣ exact-duplicate guard (kept from before)
       const setSig = JSON.stringify([...new Set(m.param)].sort());
       if (seen.weekdaySets.has(setSig)) {
         return { key: m.id, msg: 'Duplicate rule – already defined above' };
       }
       seen.weekdaySets.add(setSig);
       continue;
     }

     const sig = String(m.param);   // low_inventory / length_of_stay

     if (seen[m.type].has(sig)) {
       return {
         key: m.id,                       // let <ModifierCard> show inline
         msg: 'Duplicate rule – already defined above',
       };
     }
     seen[m.type].add(sig);
   }
   return null;
}

function shared(data = {}) {
  const e = {};

  /* ---- required primitives ------------------------------------- */
  if (isBlank(data.timezone)) add(e, 'timezone', 'Required');
  if (isBlank(data.base_currency)) add(e, 'base_currency', 'Select currency');

  /* ---- booking‑limits ------------------------------------------ */
  const bl = data.booking_limits ?? {};
  if (!posInt(bl.max_per_client_per_day)) {
    add(e, 'booking_limits.max_per_client_per_day', 'Enter a positive number');
  }

  /* ---- cut‑off & lead‑time – must be > 0 ----------------------- */
  const cutoff = data.booking_cutoff_minutes;
  if (cutoff === undefined || cutoff === null || !Number.isInteger(cutoff) || cutoff <= 0) {
    add(e, 'booking_cutoff_minutes', 'Enter minutes > 0');
  }

  const lead = data.booking_lead_days;
  if (lead === undefined || lead === null || !Number.isInteger(lead) || lead <= 0) {
    add(e, 'booking_lead_days', 'Enter days > 0');
  } else if (lead > 365) {
    add(e, 'booking_lead_days', 'Maximum 365 days');
  }

  const manual = data.manual_approval_settings || {};
  if (data.requires_manual_approval) {
    if (manual.apply_to_all === false) {
      const selected = manual.selected_offerings || {};
      const total = Object.values(selected).reduce((sum, ids) => sum + (Array.isArray(ids) ? ids.length : 0), 0);
      if (total === 0) {
        add(e, 'manual_approval_settings.selected_offerings', 'Select at least one offering or enable "All offerings".');
      }
    }
  }

  return e;
}

export function validateAll(selectedModes = [], data = {}) {
  let errors = { ...shared(data) };

  selectedModes.forEach((m) => {
    switch (m) {
      case 'personal_services':
       errors = { ...errors, ...personalServices(
         data.personal_services,
         data.employees,
         data.personal_services?.equipment  // ← pass the nested array instead
       ) };
        break;
       case 'modular_seating':
         errors = {
           ...errors,
           ...modularSeating(
               data.modular_seating,
               !!data.requires_manual_approval   // 👈 new flag
           )
         };
        break;
      case 'class_based':
        errors = { ...errors, ...classBased(data.class_based, data.employees) };
        break;
      case 'table_inventory':
        errors = { ...errors, ...tableInventory(data.table_inventory) };
        break;
      case 'accommodation':
        errors = { ...errors, ...accommodation(data.accommodation) };
        break;
      
      default:
        break;
    }
  });
  errors = { ...errors, ...bundles(data.bundles) };
  // enforce global hard limits
  // services ≤ 30
  _maxCount(data.personal_services?.services, 30, 'personal_services.services', 'Services per business', errors);
  // classes ≤ 20
  _maxCount(data.class_based?.classes, 20, 'class_based.classes', 'Classes per business', errors);
  // sessions per class ≤ 50
  (data.class_based?.classes || []).forEach((c, i) => {
    _maxCount(c?.sessions, 50, `class${i}.sessions`, 'Sessions per class', errors);
  });
  // seating configs ≤ 14
  _maxCount(data.modular_seating, 14, 'modular_seating', 'Seating configurations', errors);
  // per-config tables ≤ 15
  (data.modular_seating || []).forEach((cfg, idx) => {
    _maxCount(cfg?.tableTypes, 15, `seating${idx}.tableTypes`, 'Table types per configuration', errors);
  });
  // bundles ≤ 20 and items per bundle ≤ 10
  _maxCount(data.bundles, 20, 'bundles', 'Bundles', errors);
  (data.bundles || []).forEach((b, i) => {
    const cnt = Object.values(b?.items || {}).reduce((s, arr) => s + ((arr || []).length), 0);
    if (cnt > 10) add(errors, `bundles${i}.items`, 'Max 10 items per bundle');
  });
  // employees per service ≤ 50
  (data.personal_services?.services || []).forEach((s, i) => {
    if ((s.allowed_employees || []).length > 50) add(errors, `svc${i}.employees`, 'Max 50 employees per service');
  });
  // exceptions per rule ≤ min(50, lead_days)
  const maxEx = Math.min(50, Number.isInteger(data.booking_lead_days) ? data.booking_lead_days : 0);
  (data.operational_hours_rules || []).forEach((r, i) => {
    const exCount = r?.hours?.exceptions?.length || 0;
    if (exCount > maxEx) add(errors, `operational_hours_rules${i}.hours`, `Max ${maxEx} exceptions for this rule`);
  });
  // fee policies ≤ 20
  _maxCount(data.fee_policies, 20, 'fee_policies', 'Fee policies', errors);
  // edit rules ≤ 20
  _maxCount(data.edit_rules, 20, 'edit_rules', 'Edit rules', errors);
  errors = { ...errors, ...feePolicies(data.fee_policies, data) };
  errors = { ...errors,
             ...operationalHours(data.operational_hours_rules, data, selectedModes) };
  errors = { ...errors, ...taxesAndFees(data.taxes_fees, data) };
  errors = { ...errors, ...ageRestrictions(data.age_restrictions, data) };
  errors = { ...errors,
             ...notificationsConfig(data.notifications, data, data.bundles) };

  /* Per-offering booking rules */
  errors = { ...errors, ...bookingRules(selectedModes, data) };

  /* Per-offering edit rules */
  errors = { ...errors, ...editRules(selectedModes, data) };

  return errors;
}

export const err        = (errors, key)      => errors?.[key] ?? null;
export const hasErrPref = (errors, prefix)   =>
  Object.keys(errors).some(k => k.startsWith(prefix));
export const clsErr     = (errors, key, cls = 'border-red-500') =>
  err(errors, key) ? cls : '';

/* ──────────────────────────────────────────────────────────────── */
function bookingRules(selectedModes = [], data = {}) {
  const e = {};
  const rules = data.booking_rules || [];
  const modeData = data;

  if (!Array.isArray(rules) || rules.length === 0) return e; // allow empty → then shared cutoff/lead applies

  // basic fields + scope checks similar to taxes/fees
  const taken = new Map(); // `${group}:${id}` -> idx (to prevent multiple rules covering same offering)
  let idxAll = -1;
  let hasCustom = false;

  rules.forEach((r, i) => {
    const p = `booking_rules${i}`;
    if (!Number.isInteger(r.cutoff_min) || r.cutoff_min <= 0) {
      add(e, `${p}.cutoff_min`, 'Enter minutes > 0');
    }
    if (!Number.isInteger(r.lead_days) || r.lead_days <= 0) {
      add(e, `${p}.lead_days`, 'Enter days > 0');
    }

    const scope = r.scope || 'all';
    if (scope === 'all') {
      if (idxAll === -1) idxAll = i; else add(e, `${p}.scope`, 'Only one rule can target ALL offerings');
    }
    if (scope === 'custom') {
      hasCustom = true;
      const picks = Object.values(r.applies_to || {}).reduce((s, arr) => s + (arr?.length || 0), 0);
      if (picks === 0) add(e, `${p}.scope`, 'Pick at least one offering');

      Object.entries(r.applies_to || {}).forEach(([group, ids]) => {
        ids?.forEach((id) => {
          const sig = `${group}:${id}`;
          if (taken.has(sig)) {
            const prev = taken.get(sig);
            add(e, `booking_rules${prev}.scope`, 'An offering can belong to ONE booking rule only');
            add(e, `${p}.scope`, 'An offering can belong to ONE booking rule only');
          } else {
            taken.set(sig, i);
          }
        });
      });
    }
  });

  // If there are offerings in any selected mode, ensure full coverage:
  const items = {
    personal_services: (modeData.personal_services?.services || []).map(s => s.id),
    table_inventory  : (modeData.table_inventory?.services || []).map(service => service.id),
    class_based      : (modeData.class_based?.classes || []).map(c => c.id),
    accommodation    : (modeData.accommodation?.room_types || []).map(r => r.id),
  };

  const covered = new Set();
  rules.forEach((r) => {
    if ((r.scope || 'all') === 'all') {
      Object.values(items).flat().forEach(id => covered.add(id));
    } else {
      Object.values(r.applies_to || {}).flat().forEach(id => covered.add(id));
    }
  });

  Object.entries(items).forEach(([mode, ids]) => {
    if (!selectedModes.includes(mode)) return;
    ids.forEach((id) => {
      if (!covered.has(id)) add(e, `booking_rules`, 'All offerings must have booking cutoff and lead time');
    });
  });

  // conflict: cannot combine an ALL rule with custom-picks
  if (idxAll !== -1) {
    const anyCustomPicks = rules.some((r, i) => i !== idxAll && r.scope === 'custom' && Object.values(r.applies_to || {}).some(arr => arr?.length));
    if (anyCustomPicks) {
      add(e, `booking_rules${idxAll}.scope`, 'Cannot combine “All offerings” with specific selections');
    }
  }

  return e;
}

/* ──────────────────────────────────────────────────────────────── */
function editRules(selectedModes = [], data = {}) {
  const e = {};
  const rules = data.edit_rules || [];
  if (!Array.isArray(rules)) return e;

  // If there are offerings in any selected mode but no rules yet, block save
  const itemsPre = {
    personal_services: (data.personal_services?.services || []).map(s => s.id),
    table_inventory  : (data.table_inventory?.services || []).map(service => service.id),
    class_based      : (data.class_based?.classes || []).map(c => c.id),
    accommodation    : (data.accommodation?.room_types || []).map(r => r.id),
  };
  const totalOfferingsSelected = Object.entries(itemsPre)
    .reduce((sum, [mode, ids]) => sum + (selectedModes.includes(mode) ? ids.length : 0), 0);
  if (totalOfferingsSelected > 0 && rules.length === 0) {
    add(e, 'edit_rules', 'All offerings must have edit rules');
    return e;
  }

  // base shape checks
  const taken = new Map(); // `${group}:${id}` -> idx
  let idxAll = -1;

  rules.forEach((r, i) => {
    const p = `edit_rules${i}`;
    // Requireds
    if (isBlank(r.label)) add(e, `${p}.label`, 'Label required');
    if (!['all','custom'].includes(r.scope)) add(e, `${p}.scope`, 'Scope required');
    if (typeof r.allow_party_size_changes !== 'boolean') add(e, `${p}.allow_party_size_changes`, 'Required');
    if (typeof r.allow_switch_service !== 'boolean') add(e, `${p}.allow_switch_service`, 'Required');
    if (typeof r.allow_switch_class !== 'boolean') add(e, `${p}.allow_switch_class`, 'Required');
    if (typeof r.allow_change_room_type !== 'boolean') add(e, `${p}.allow_change_room_type`, 'Required');
    if (typeof r.allow_change_seating_area !== 'boolean') add(e, `${p}.allow_change_seating_area`, 'Required');

    // Bounds
    if (!Number.isInteger(r.max_date_shift_days) || r.max_date_shift_days < 0 || r.max_date_shift_days > 365)
      add(e, `${p}.max_date_shift_days`, 'Enter days between 0 and 365');

    if (!['hours','days'].includes(r.edit_cutoff_unit))
      add(e, `${p}.edit_cutoff_unit`, 'Select hours/days');
    else if (!Number.isInteger(r.edit_cutoff_value) || r.edit_cutoff_value < 0 ||
            (r.edit_cutoff_unit === 'hours' && r.edit_cutoff_value > 720) ||
            (r.edit_cutoff_unit === 'days' && r.edit_cutoff_value > 365))
      add(e, `${p}.edit_cutoff_value`, r.edit_cutoff_unit === 'hours' ? '0–720 hours' : '0–365 days');

    if (!['percent','fixed'].includes(r.edit_fee_type))
      add(e, `${p}.edit_fee_type`, 'Select fee type');
    else if (typeof r.edit_fee_amount !== 'number' || r.edit_fee_amount < 0 ||
            (r.edit_fee_type === 'percent' && r.edit_fee_amount > 100) ||
            (r.edit_fee_type === 'fixed' && r.edit_fee_amount > 10000))
      add(e, `${p}.edit_fee_amount`, r.edit_fee_type === 'percent' ? '0–100%' : '≤ 10000');

    if (!Number.isInteger(r.max_edits_per_reservation) || r.max_edits_per_reservation < 0 || r.max_edits_per_reservation > 10)
      add(e, `${p}.max_edits_per_reservation`, '0–10 edits');

    const scope = r.scope || 'all';
    if (scope === 'all') {
      if (idxAll === -1) idxAll = i; else add(e, `${p}.scope`, 'Only one rule can target ALL offerings');
    } else if (scope === 'custom') {
      const total = Object.values(r.applies_to || {}).reduce((s, arr) => s + (arr?.length || 0), 0);
      if (total === 0) add(e, `${p}.scope`, 'Pick at least one offering');
      Object.entries(r.applies_to || {}).forEach(([group, ids]) => {
        (ids || []).forEach(id => {
          const sig = `${group}:${id}`;
          if (taken.has(sig)) {
            const prev = taken.get(sig);
            add(e, `edit_rules${prev}.scope`, 'Offering appears in multiple edit rules');
            add(e, `${p}.scope`, 'Offering appears in multiple edit rules');
          } else {
            taken.set(sig, i);
          }
        });
      });
    }
  });

  // ensure every offering (except bundles) is covered by some rule
  const items = {
    personal_services: (data.personal_services?.services || []).map(s => s.id),
    table_inventory  : (data.table_inventory?.services || []).map(service => service.id),
    class_based      : (data.class_based?.classes || []).map(c => c.id),
    accommodation    : (data.accommodation?.room_types || []).map(r => r.id),
  };
  const covered = new Set();
  rules.forEach((r) => {
    if ((r.scope || 'all') === 'all') {
      Object.entries(items).forEach(([mode, ids]) => {
        if (!selectedModes.includes(mode)) return;
        ids.forEach(id => covered.add(`${mode}:${id}`));
      });
    } else {
      Object.entries(r.applies_to || {}).forEach(([mode, ids]) => {
        (ids || []).forEach(id => covered.add(`${mode}:${id}`));
      });
    }
  });
  Object.entries(items).forEach(([mode, ids]) => {
    if (!selectedModes.includes(mode)) return;
    ids.forEach((id) => {
      if (!covered.has(`${mode}:${id}`)) add(e, `edit_rules`, 'All offerings must have edit rules');
    });
  });

  // conflict: cannot combine ALL with specific selections
  if (idxAll !== -1) {
    const anyCustom = rules.some((r, i) => i !== idxAll && r.scope === 'custom' && Object.values(r.applies_to || {}).some(arr => arr?.length));
    if (anyCustom) add(e, `edit_rules${idxAll}.scope`, 'Cannot combine “All offerings” with specific selections');
  }

  return e;
}
